import { TimeField } from "@/components/TimeField";
import { formatDuration, minutesSinceMidnight } from "@/lib/clock";
import type { WorkdayLogConfig } from "@/lib/schema";
import type { WidgetSettingsEditorProps } from "@/widgets/types";
import { isWorkDuration } from "./duration";
import { earnedPeriods, hoursPoints } from "./scoring";

type Props = WidgetSettingsEditorProps<WorkdayLogConfig>;

// No `scoringField`: a kind that declares parts owns its week goals, so the
// modal renders no shared Ukesmål for it (docs/adr/0010).
export function SettingsEditor({ config, onChange, errors = {} }: Props) {
  const fieldError = (key: string) =>
    errors[key] ? (
      <p className="field-error" data-testid={`widget-error-${key}`}>
        {errors[key]}
      </p>
    ) : null;

  const numberPatch = (key: keyof WorkdayLogConfig) => (value: string) =>
    onChange({
      [key]: value === "" ? Number.NaN : Number(value),
    } as Partial<WorkdayLogConfig>);

  // Each part carries its own goal; `weekGoal` is their sum, written here
  // rather than typed. NaN in either half keeps the total NaN, which is what
  // blocks Save until both are filled in.
  const goalPatch =
    (key: "hoursWeekGoal" | "periodsWeekGoal") => (value: string) => {
      const next = value === "" ? Number.NaN : Number(value);
      const hours = key === "hoursWeekGoal" ? next : config.hoursWeekGoal;
      const periods = key === "periodsWeekGoal" ? next : config.periodsWeekGoal;
      onChange({
        [key]: next,
        weekGoal: hours + periods,
      } as Partial<WorkdayLogConfig>);
    };

  // What a day exactly at the target is worth. Recomputed live so a period
  // length that quietly costs a period — 04:00 against a 07:30 target earns
  // one, not two — is visible while you are still editing it.
  const targetMinutes = minutesSinceMidnight(config.targetDuration);
  const fullDay = { duration: config.targetDuration };
  const fullDayHours = hoursPoints(config, fullDay);
  const fullDayPeriods = earnedPeriods(config, fullDay);
  const fullDayTotal = Number.isFinite(config.pointsPerPeriod)
    ? fullDayHours + fullDayPeriods * config.pointsPerPeriod
    : fullDayHours;

  return (
    <div
      className="widget-settings"
      data-testid={`widget-settings-${config.id}`}
    >
      <div className="setting-row">
        <label className="setting-label" htmlFor={`label-${config.id}`}>
          Tittel
        </label>
        <div className="setting-control">
          <input
            id={`label-${config.id}`}
            className="input"
            type="text"
            value={config.label}
            onChange={(e) => onChange({ label: e.target.value })}
            data-testid="widget-label-input"
          />
          {fieldError("label")}
        </div>
      </div>

      <div className="setting-row">
        <label className="setting-label" htmlFor={`target-${config.id}`}>
          Normal arbeidsdag
          <small>Hvor lang en full dag er. Lengre dager gir mer.</small>
        </label>
        <div className="setting-control">
          <TimeField
            id={`target-${config.id}`}
            value={config.targetDuration}
            accept={isWorkDuration}
            onChange={(next) => onChange({ targetDuration: next ?? "" })}
            data-testid="workday-log-target-input"
          />
          {fieldError("targetDuration")}
        </div>
      </div>

      <div className="setting-row">
        <label className="setting-label" htmlFor={`points-${config.id}`}>
          Poeng for en full dag
          <small>Kortere og lengre dager får forholdsmessig uttelling.</small>
        </label>
        <div className="setting-control">
          <div className="num-input">
            <input
              id={`points-${config.id}`}
              type="number"
              min={0.25}
              step={0.25}
              value={
                Number.isFinite(config.pointsAtTarget)
                  ? config.pointsAtTarget
                  : ""
              }
              onChange={(e) => numberPatch("pointsAtTarget")(e.target.value)}
              data-testid="workday-log-points-input"
            />
            <span className="unit">p</span>
          </div>
          {fieldError("pointsAtTarget")}
        </div>
      </div>

      <div className="setting-row">
        <label className="setting-label" htmlFor={`period-${config.id}`}>
          Lengde på en periode
          <small>
            Hver hele periode du jobber gir ett spørsmål om du var produktiv.
          </small>
        </label>
        <div className="setting-control">
          <TimeField
            id={`period-${config.id}`}
            value={config.periodDuration}
            accept={isWorkDuration}
            onChange={(next) => onChange({ periodDuration: next ?? "" })}
            data-testid="workday-log-period-input"
          />
          {fieldError("periodDuration")}
        </div>
      </div>

      <div className="setting-row">
        <label className="setting-label" htmlFor={`periodpoints-${config.id}`}>
          Poeng per produktiv periode
          <small>Gis for hver periode du svarer Ja på.</small>
        </label>
        <div className="setting-control">
          <div className="num-input">
            <input
              id={`periodpoints-${config.id}`}
              type="number"
              min={0.25}
              step={0.25}
              value={
                Number.isFinite(config.pointsPerPeriod)
                  ? config.pointsPerPeriod
                  : ""
              }
              onChange={(e) => numberPatch("pointsPerPeriod")(e.target.value)}
              data-testid="workday-log-period-points-input"
            />
            <span className="unit">p</span>
          </div>
          {fieldError("pointsPerPeriod")}
        </div>
      </div>

      <div className="setting-row">
        <label className="setting-label" htmlFor={`hoursgoal-${config.id}`}>
          Ukesmål · arbeidstid
          <small>Følges som egen linje i ukesoppsummeringen.</small>
        </label>
        <div className="setting-control">
          <div className="num-input">
            <input
              id={`hoursgoal-${config.id}`}
              type="number"
              min={0}
              value={
                Number.isFinite(config.hoursWeekGoal)
                  ? config.hoursWeekGoal
                  : ""
              }
              onChange={(e) => goalPatch("hoursWeekGoal")(e.target.value)}
              data-testid="workday-log-hours-weekgoal-input"
            />
            <span className="unit">p</span>
          </div>
          {fieldError("hoursWeekGoal")}
        </div>
      </div>

      <div className="setting-row">
        <label className="setting-label" htmlFor={`periodsgoal-${config.id}`}>
          Ukesmål · produktivitet
          <small>Følges som egen linje i ukesoppsummeringen.</small>
        </label>
        <div className="setting-control">
          <div className="num-input">
            <input
              id={`periodsgoal-${config.id}`}
              type="number"
              min={0}
              value={
                Number.isFinite(config.periodsWeekGoal)
                  ? config.periodsWeekGoal
                  : ""
              }
              onChange={(e) => goalPatch("periodsWeekGoal")(e.target.value)}
              data-testid="workday-log-periods-weekgoal-input"
            />
            <span className="unit">p</span>
          </div>
          {fieldError("periodsWeekGoal")}
        </div>
      </div>

      <div
        className="workday-log-summary"
        data-testid={`workday-log-summary-${config.id}`}
      >
        <b>Sammendrag</b>
        {targetMinutes !== null && targetMinutes > 0 ? (
          <>
            <span>
              En full dag ({formatDuration(targetMinutes)}): {fullDayHours}p for
              tiden + {fullDayPeriods}{" "}
              {fullDayPeriods === 1 ? "periode" : "perioder"} ={" "}
              <b>{fullDayTotal}p</b>
            </span>
            <span>Maks per uke: ingen øvre grense — lengre dager gir mer.</span>
          </>
        ) : (
          <span>Fyll ut normal arbeidsdag for å se uttellingen.</span>
        )}
        <span data-testid={`workday-log-total-goal-${config.id}`}>
          {Number.isFinite(config.weekGoal)
            ? `Samlet ukesmål: ${config.weekGoal}p`
            : "Samlet ukesmål: fyll ut begge delmålene."}
        </span>
      </div>
    </div>
  );
}
